"use client";

import { AnimatePresence, m as motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

type Status = "idle" | "submitting" | "success" | "error";

type FieldErrors = Partial<Record<"name" | "email" | "message", string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm() {
  const t = useTranslations("contact");
  const prefersReduced = useReducedMotion();

  const intentId = useId();
  const nameId = useId();
  const emailId = useId();
  const messageId = useId();

  const intentOptions = useMemo(() => {
    try {
      const raw = t.raw("intent.items");
      return Array.isArray(raw) ? (raw as string[]) : [];
    } catch {
      return [] as string[];
    }
  }, [t]);

  const [intent, setIntent] = useState(intentOptions[0] ?? "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  // Honeypot — invisible to humans, frequently auto-filled by bots.
  // Field name is "homepage_url" (not the canonical "website") because
  // off-the-shelf bots began pre-filtering the common name; this slows
  // them down marginally. Never read by the user, never displayed; just
  // submitted with the body and rejected server-side if non-empty.
  const [homepageUrl, setHomepageUrl] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<FieldErrors>({});

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = t("formValidation.nameRequired");
    if (!email.trim()) next.email = t("formValidation.emailRequired");
    else if (!EMAIL_RE.test(email.trim()))
      next.email = t("formValidation.emailInvalid");
    if (!message.trim()) next.message = t("formValidation.messageRequired");
    return next;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus("idle");
      return;
    }

    setStatus("submitting");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          intent,
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          homepage_url: homepageUrl,
        }),
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      setStatus("success");
      setName("");
      setEmail("");
      setMessage("");
      setHomepageUrl("");
      setIntent(intentOptions[0] ?? "");
    } catch {
      setStatus("error");
    }
  }

  const isSubmitting = status === "submitting";
  const submitLabel = isSubmitting ? t("formSubmitting") : t("formSubmit");

  const hasIntent = intentOptions.length > 0;

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      aria-busy={isSubmitting}
      className="rounded-3xl border p-6 md:p-10"
      style={{
        borderColor: "var(--border)",
        background: "var(--bg-elevated)",
      }}
    >
      <div className="flex flex-col gap-5">
        {hasIntent ? (
          <Field id={intentId} label={t("intent.label")}>
            <IntentSelect
              id={intentId}
              options={intentOptions}
              value={intent}
              onChange={setIntent}
              disabled={isSubmitting}
              reducedMotion={Boolean(prefersReduced)}
            />
          </Field>
        ) : null}

        <Field
          id={nameId}
          label={t("formLabel_name")}
          error={errors.name}
          describedById={`${nameId}-error`}
        >
          <input
            id={nameId}
            type="text"
            name="name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("formPlaceholder_name")}
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.name) || undefined}
            aria-describedby={errors.name ? `${nameId}-error` : undefined}
            className="w-full rounded-xl border bg-transparent px-4 py-3 text-[15px] text-fg1 placeholder:text-fg4 focus:outline-none disabled:opacity-60"
            style={{ borderColor: "var(--border-strong)" }}
          />
        </Field>

        <Field
          id={emailId}
          label={t("formLabel_email")}
          error={errors.email}
          describedById={`${emailId}-error`}
        >
          <input
            id={emailId}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("formPlaceholder_email")}
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.email) || undefined}
            aria-describedby={errors.email ? `${emailId}-error` : undefined}
            className="w-full rounded-xl border bg-transparent px-4 py-3 text-[15px] text-fg1 placeholder:text-fg4 focus:outline-none disabled:opacity-60"
            style={{ borderColor: "var(--border-strong)" }}
          />
        </Field>

        <Field
          id={messageId}
          label={t("formLabel_message")}
          error={errors.message}
          describedById={`${messageId}-error`}
        >
          <textarea
            id={messageId}
            name="message"
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("formPlaceholder_message")}
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.message) || undefined}
            aria-describedby={
              errors.message ? `${messageId}-error` : undefined
            }
            className="w-full resize-y rounded-xl border bg-transparent px-4 py-3 text-[15px] text-fg1 placeholder:text-fg4 focus:outline-none disabled:opacity-60"
            style={{ borderColor: "var(--border-strong)" }}
          />
        </Field>

        {/* Honeypot — bots fill this; humans never see it */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            top: "auto",
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
        >
          <label htmlFor="homepage-url-hp">Homepage URL</label>
          <input
            id="homepage-url-hp"
            type="text"
            name="homepage_url"
            tabIndex={-1}
            autoComplete="off"
            value={homepageUrl}
            onChange={(e) => setHomepageUrl(e.target.value)}
          />
        </div>

        <div className="mt-3 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-display text-[15px] font-semibold text-white transition-opacity disabled:opacity-70 sm:w-auto"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-electric) 0%, var(--brand-azure) 100%)",
              boxShadow:
                "0 0 0 1px rgba(59,126,255,0.45), 0 12px 32px rgba(30,63,232,0.35)",
              border: "none",
            }}
          >
            <span>{submitLabel}</span>
            <span aria-hidden>{isSubmitting ? "…" : "→"}</span>
          </button>
        </div>

        <div aria-live="polite" className="min-h-[1.5rem]">
          <AnimatePresence mode="wait" initial={false}>
            {status === "success" ? (
              <StatusLine
                key="success"
                tone="success"
                text={t("formStatus.success")}
                reduced={Boolean(prefersReduced)}
              />
            ) : status === "error" ? (
              <StatusLine
                key="error"
                tone="error"
                text={t("formStatus.error")}
                reduced={Boolean(prefersReduced)}
              />
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </form>
  );
}

type FieldProps = {
  id: string;
  label: string;
  error?: string;
  describedById?: string;
  children: React.ReactNode;
};

function Field({ id, label, error, describedById, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="font-mono text-[11px] uppercase text-fg3"
        style={{ letterSpacing: "0.2em" }}
      >
        {label}
      </label>
      {children}
      {error && describedById ? (
        <span
          id={describedById}
          className="inline-flex items-center gap-2 text-[13px] leading-snug"
          style={{ color: "var(--danger)" }}
        >
          <span
            aria-hidden
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[11px] font-bold leading-none"
            style={{
              background: "var(--danger)",
              color: "#0A0A0F",
            }}
          >
            !
          </span>
          <span>{error}</span>
        </span>
      ) : null}
    </div>
  );
}

type IntentSelectProps = {
  id: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  reducedMotion: boolean;
};

function IntentSelect({
  id,
  options,
  value,
  onChange,
  disabled,
  reducedMotion,
}: IntentSelectProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(() => {
    const i = options.indexOf(value);
    return i >= 0 ? i : 0;
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listboxId = `${id}-listbox`;
  const optionId = (i: number) => `${id}-option-${i}`;

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent | TouchEvent) {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
    };
  }, [open]);

  function openDropdown() {
    const i = options.indexOf(value);
    setActiveIndex(i >= 0 ? i : 0);
    setOpen(true);
  }

  function commit(i: number) {
    const next = options[i];
    if (next != null) onChange(next);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    const last = options.length - 1;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        openDropdown();
        return;
      }
      setActiveIndex((i) => Math.min(last, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openDropdown();
        return;
      }
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (event.key === "Home") {
      if (open) {
        event.preventDefault();
        setActiveIndex(0);
      }
    } else if (event.key === "End") {
      if (open) {
        event.preventDefault();
        setActiveIndex(last);
      }
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (open) commit(activeIndex);
      else openDropdown();
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    } else if (event.key === "Tab") {
      if (open) setOpen(false);
    }
  }

  const motionPanel = !reducedMotion;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open ? optionId(activeIndex) : undefined}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) setOpen(false);
          else openDropdown();
        }}
        onKeyDown={onKeyDown}
        className="flex w-full items-center justify-between gap-3 rounded-xl border bg-transparent px-4 py-3 text-left text-[15px] text-fg1 transition-colors focus-visible:outline-none disabled:opacity-60"
        style={{
          borderColor: open ? "var(--brand-azure)" : "var(--border-strong)",
          boxShadow: open
            ? "0 0 0 3px color-mix(in srgb, var(--brand-azure) 22%, transparent)"
            : "none",
        }}
      >
        <span className="truncate">{value || options[0] || ""}</span>
        <span
          aria-hidden
          className="font-mono text-[12px] text-fg3"
          style={{
            transition: reducedMotion
              ? undefined
              : "transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            display: "inline-block",
          }}
        >
          ▾
        </span>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            ref={panelRef}
            initial={motionPanel ? { opacity: 0, y: -6 } : false}
            animate={motionPanel ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
            exit={motionPanel ? { opacity: 0, y: -6 } : { opacity: 0 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-xl border"
            style={{
              top: "100%",
              borderColor: "var(--border-strong)",
              background: "var(--bg)",
              boxShadow:
                "0 18px 40px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.25)",
            }}
          >
            <ul
              id={listboxId}
              role="listbox"
              aria-labelledby={id}
              className="m-0 list-none p-1.5"
            >
              {options.map((option, i) => {
                const isActive = i === activeIndex;
                const isSelected = option === value;
                return (
                  <li
                    key={option}
                    id={optionId(i)}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(i);
                    }}
                    className="flex cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 text-[15px] transition-colors"
                    style={{
                      background: isActive
                        ? "var(--bg-elevated)"
                        : "transparent",
                      color: isSelected
                        ? "var(--brand-azure)"
                        : "var(--fg1)",
                    }}
                  >
                    <span className="truncate">{option}</span>
                    {isSelected ? (
                      <span
                        aria-hidden
                        className="ml-3 font-mono text-[11px]"
                        style={{ color: "var(--brand-azure)" }}
                      >
                        ●
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

type StatusLineProps = {
  tone: "success" | "error";
  text: string;
  reduced: boolean;
};

function StatusLine({ tone, text, reduced }: StatusLineProps) {
  const color =
    tone === "success" ? "var(--brand-azure)" : "var(--brand-cyan)";
  const base = {
    margin: 0,
    fontFamily:
      "var(--font-jetbrains-mono), ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    textTransform: "uppercase" as const,
    letterSpacing: "0.18em",
    color,
  };

  if (reduced) {
    return <p style={base}>{text}</p>;
  }

  return (
    <motion.p
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      style={base}
    >
      {text}
    </motion.p>
  );
}
